import {
  Component,
  Input,
  ViewChild,
  OnChanges,
  SimpleChanges,
  AfterViewInit,
  ContentChild,
  TemplateRef,
  Output,
  EventEmitter,
  OnInit,
} from '@angular/core';
import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { DateTime } from 'luxon';
import { Helpers } from 'src/app/utils/helpers';

export type TExpandableTableConfig = {
  columns: {
    key: string,
    title: string,
    sortable?: boolean,
    sortAsDate?: boolean,
    dateFormat?: string,

    /**
     * Column type. Defaults to `text`
     */
    type?: 'text' | 'icon';

    /**
     * Function to determine what icon to display.
     * If this is an orchid icon, then path should just be the name of the icon, e.g. "checkmark"
     * Color can be a color name like "red", "green", etc, or it can be a css var like "var(--positive-3)"
     */
    icon?: (data: any) => {
      isOrchid: boolean,
      icon: string,
      color?: string,
      iconSuffix?: string,
    };
  }[];
  primarySort: {
    key: string;
    direction: 'asc' | 'desc',
    /**
     * Setting this to true will cause the table to sort by
     * the selected column and then sort the result of that
     * by the primary column.
     */
    retainSort?: boolean,
  };
  secondarySort?: string[];
};

@Component({
  selector: 'app-pol-expandable-table',
  templateUrl: './pol-expandable-table.component.html',
  styleUrls: ['./pol-expandable-table.component.scss'],
})
export class PolExpandableTableComponent implements OnChanges, AfterViewInit, OnInit {
  @Input() config: TExpandableTableConfig = {
    columns: [],
    primarySort: {
      key: '',
      direction: 'asc',
    },
  };

  @Input() data: any[] = [];
  @Input() viewportHeight: number = 400;
  @Input() tableMessage: string = 'No Data Available'; // Default message for no data
  @Input() isLoading: boolean = false; // Loading state
  @Output() rowExpanded = new EventEmitter<any>;
  @Input() resetSorting: boolean = false;

  @ContentChild(TemplateRef) expandedTemplate: TemplateRef<any>;

  @ViewChild(CdkVirtualScrollViewport) viewport: CdkVirtualScrollViewport;

  expandedRow: number | null = null; // Tracks the currently expanded row index

  sortKey: string;
  sortDirection: 'asc' | 'desc';

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['data']) {
      // this.expandedRow = null; // Reset expanded row when data changes
      this.sortData(); // Sort data
    }
    if (changes['resetSorting']) {
      this.initializeSorting(); // Initialize sorting on data change
    }
  }

  ngOnInit() {
    this.initializeSorting();
  }

  ngAfterViewInit(): void {
    if (this.viewport) {
      this.viewport.checkViewportSize(); // Ensures correct rendering on load
    }
  }

  initializeSorting(): void {
    this.sortKey = this.config.primarySort?.key;
    this.sortDirection = this.config.primarySort?.direction;
  }

  toggleRow(index: number): void {
    if (this.expandedRow !== index) {
      // Log the row information only when expanding
      this.onRowClick(this.data[index]);
    }

    this.expandedRow = this.expandedRow === index ? null : index; // Toggle expand/collapse
  }

  onRowClick(row: any): void {
    this.rowExpanded.emit(row);
  }

  sortColumn(key: string): void {
    this.expandedRow = null; // Reset expanded row when sorting updated for either of the column
    if (this.sortKey === key) {
      // Toggle sort direction if the same column is clicked
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      // Set new sort key
      this.sortKey = key;
      this.sortDirection = 'asc'; // Default to ascending
    }

    this.sortData();
  }

  sortData(): void {
    const sortFunction = (a: any, b: any, col: string) => {
      let aa = a[col];
      let bb = b[col];

      // handle undefined or null values
      if (aa === undefined || aa === null) aa = '';
      if (bb === undefined || bb === null) bb = '';

      // for numeric values
      if (!isNaN(Number(aa)) && !isNaN(Number(bb))) {
        aa = Number(aa);
        bb = Number(bb);
      }

      if (aa < bb) return this.sortDirection === 'asc' ? -1 : 1;
      if (aa > bb) return this.sortDirection === 'asc' ? 1 : -1;
      return 0;
    };

    const sortDateFunction = (a: any, b: any, col: string) => {
      const sortedColumn = this.config.columns.find(i => i.key === col);
      const aa = sortedColumn?.dateFormat ? DateTime.fromFormat(a[col], sortedColumn.dateFormat) : DateTime.fromISO(a[col]);
      const bb = sortedColumn?.dateFormat ? DateTime.fromFormat(b[col], sortedColumn.dateFormat) : DateTime.fromISO(b[col]);

      if (!aa.isValid) {
        console.warn(`ExpandableTableComponent | column: ${col} | attempted to convert ${a[col]} to a date, but failed bacause ${aa.invalidReason} \nExplanation: ${aa.invalidExplanation} \nSuggestion: set the dateFormat property on this column`);
        return this.sortDirection === 'asc' ? -1 : 1;
      }

      if (!bb.isValid) {
        console.warn(`ExpandableTableComponent | column: ${col} | attempted to convert ${b[col]} to a date, but failed bacause ${bb.invalidReason} \nExplanation: ${bb.invalidExplanation} \nSuggestion: set the dateFormat property on this column`);
        return this.sortDirection === 'asc' ? 1 : -1;
      }

      if (aa < bb) {
        return this.sortDirection === 'asc' ? -1 : 1;
      } else if (aa > bb) {
        return this.sortDirection === 'asc' ? 1 : -1;
      } else {
        return 0;
      }
    };

    const sortedColumn = this.config.columns.find(i => i.key === this.sortKey);

    // sort date columns
    if (sortedColumn?.sortAsDate) {
      this.data.sort((a, b) => sortDateFunction(a, b, this.sortKey));
    } else { // all other columns
      // secondary sorts
      if (this.config.secondarySort) {
        const ssort = Helpers.copy(this.config.secondarySort).reverse();

        for (const sort of ssort) {
          this.data.sort((a, b) => sortFunction(a, b, sort.toString()));
        }
      }

      if (this.config.primarySort.retainSort && this.sortKey !== this.config.primarySort.key) {
        // after sorting secondary columns, sort by the original primary column
        const cachedSortDir = this.sortDirection;
        this.sortDirection = this.config.primarySort.direction;
        const sortColumn = this.config.columns.find(i => i.key === this.config.primarySort.key);

        if (sortColumn.sortAsDate) {
          this.data.sort((a, b) => sortDateFunction(a, b, this.config.primarySort.key));
        } else {
          this.data.sort((a, b) => sortFunction(a, b, this.config.primarySort.key));
        }

        this.sortDirection = cachedSortDir;
      }

      // primary sort
      this.data.sort((a, b) => sortFunction(a, b, this.sortKey));
    }

    // trigger change detection
    this.data = Helpers.copy(this.data);

    setTimeout(() => {
      this.refreshViewport();
    }, 0);
  }

  compareValues(valueA: any, valueB: any): number {
    // Handle null or undefined values
    if (valueA == null || valueB == null) {
      return valueA == null ? 1 : -1;
    }

    // Handle string comparison
    if (typeof valueA === 'string' && typeof valueB === 'string') {
      valueA = valueA.toLowerCase();
      valueB = valueB.toLowerCase();
    }

    // Numeric and generic comparison
    if (valueA < valueB) {
      return this.sortDirection === 'asc' ? -1 : 1;
    }
    if (valueA > valueB) {
      return this.sortDirection === 'asc' ? 1 : -1;
    }
    return 0; // Equal values
  }

  private refreshViewport(): void {
    if (this.viewport) {
      if (this.data && this.data.length > 0) {
        // Enable scrolling if there is data
        this.viewport.checkViewportSize();
        this.viewport.scrollToIndex(0);
        const range = this.viewport.getRenderedRange();
        this.viewport.setRenderedRange({ start: 1, end: 2 });
        this.viewport.setRenderedRange(range);
      } else {
        // Disable scrolling if there is no data
        this.viewport.setTotalContentSize(0);
        this.viewport.checkViewportSize();
        this.viewport.scrollToIndex(0);
        this.viewport.setTotalContentSize(0);
      }
    }
  }
}

